import Purchases from 'react-native-purchases'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { deleteAccount } from '../dal/user-dal'
import { hasOwnedRevenueCatAccess } from '../services/entitlement-utils'
import { queryKeys } from './query-keys'
import { userEntitlementSyncQueryOptions } from './user-queries'

export const customerInfoQueryOptions = (authUserId: string | null) =>
  queryOptions({
    queryKey: queryKeys.user.customerInfo(authUserId),
    queryFn: () => Purchases.getCustomerInfo(),
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  })

export interface DeletionEligibility {
  eligible: boolean
  reason: 'eligible' | 'active_subscription' | 'unknown'
}

function getDeletionEligibilityFromCustomerInfo(
  customerInfo:
    Awaited<ReturnType<typeof Purchases.getCustomerInfo>> | undefined,
  revenueCatId: string | null | undefined,
): DeletionEligibility {
  if (!customerInfo) return { eligible: false, reason: 'unknown' }

  const hasAccess = hasOwnedRevenueCatAccess({
    customerInfo,
    revenueCatId,
  })
  if (hasAccess) {
    const willRenew =
      Object.values(customerInfo.entitlements?.active ?? {}).some(
        (e) => e.willRenew,
      ) ?? false

    if (willRenew) {
      return { eligible: false, reason: 'active_subscription' }
    }
  }

  return { eligible: true, reason: 'eligible' }
}

export const deletionEligibilityQueryOptions = (
  authUserId: string | null,
  revenueCatId: string | null | undefined,
) =>
  queryOptions({
    queryKey: queryKeys.user.deletionEligibility(authUserId),
    queryFn: async () => {
      const customerInfo = await Purchases.getCustomerInfo()
      return getDeletionEligibilityFromCustomerInfo(customerInfo, revenueCatId)
    },
    staleTime: 0,
  })

export function useRestorePurchases(
  authUserId: string | null,
  revenueCatId: string | null | undefined,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => Purchases.restorePurchases(),
    onSuccess: async (restoredInfo) => {
      queryClient.setQueryData(
        customerInfoQueryOptions(authUserId).queryKey,
        restoredInfo,
      )
      queryClient.setQueryData(
        deletionEligibilityQueryOptions(authUserId, revenueCatId).queryKey,
        getDeletionEligibilityFromCustomerInfo(restoredInfo, revenueCatId),
      )

      if (authUserId == null) {
        return
      }

      try {
        const syncedUser = await queryClient.fetchQuery(
          userEntitlementSyncQueryOptions(authUserId),
        )
        queryClient.setQueryData(queryKeys.user.profile(authUserId), syncedUser)
      } catch (error) {
        console.warn(
          'Failed to sync user profile after restoring purchases',
          error,
        )
      }
    },
  })
}

export function useDeleteAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.clear()
    },
  })
}
